"use client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { signIn } from "next-auth/react"
import type { SocialProvider } from "@/lib/auth/social/types"

type SocialOptions = {
  providers: SocialProvider[]
  /** Store-agnostic Shopify admin link that opens the app and signs a store owner in. */
  shopifyAppUrl: string | null
  pending: { provider: SocialProvider; name: string; email: string | null; returnTo: string; verificationEmail?: string } | null
}
const queryKey = ["auth", "social-options"]

export function useSocialAuth() {
  const client = useQueryClient()
  const options = useQuery<SocialOptions>({
    queryKey, queryFn: async () => {
      const response = await fetch("/api/identity/social", { credentials: "same-origin", cache: "no-store" })
      if (!response.ok) throw new Error("Unable to load sign-in options")
      return response.json()
    }, staleTime: 0, retry: false,
  })
  const start = useMutation({ mutationFn: async (input: { provider: SocialProvider; rememberMe: boolean; returnTo: string }) => {
    await post({ action: "start", ...input })
    await signIn(input.provider, { callbackUrl: "/" })
  } })
  const link = useMutation({ mutationFn: async (password: string) => {
    await post({ action: "link", password })
    await client.invalidateQueries({ queryKey })
  } })
  return { options, start, link }
}

async function post(body: unknown) {
  const response = await fetch("/api/identity/social", {
    method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  })
  const result = await response.json()
  if (!response.ok) throw new Error(result.error || "Unable to complete social sign-in")
  return result
}
